import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import type { Options } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import type { Document } from '@contentful/rich-text-types';
import { Col, Row } from '@opendoor/atoms/box';
import { Link } from '@opendoor/atoms/link';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import type { EntryComponent } from 'cms/entries/entries';
import type {
  ILpComponentNarrativeLockups,
  ILpElementNarrativeLockup,
} from 'declarations/contentful';

import Container from './shared/Container';

const hasText = (node: unknown): boolean => {
  if (!node || typeof node !== 'object') {
    return false;
  }

  const richTextNode = node as { value?: unknown; content?: unknown[] };

  if (typeof richTextNode.value === 'string' && richTextNode.value.trim()) {
    return true;
  }

  return richTextNode.content?.some((child) => hasText(child)) ?? false;
};

const isEmptyRichText = (document?: Document) => {
  if (!document?.content?.length) {
    return true;
  }

  return !document.content.some((node) => hasText(node));
};

const richTextOptions = (analyticsId?: string): Options => ({
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => (
      <Text as="p" className="text-body-lg text-od-content-primary">
        {children}
      </Text>
    ),
    [BLOCKS.HEADING_2]: (_, children) => (
      <Text as="h3" className="text-subheader-md text-od-content-primary">
        {children}
      </Text>
    ),
    [BLOCKS.HEADING_3]: (_, children) => (
      <Text as="h4" className="text-subheader-sm text-od-content-primary">
        {children}
      </Text>
    ),
    [BLOCKS.UL_LIST]: (_, children) => (
      <Col role="list" className="gap-1.5 pl-5">
        {children}
      </Col>
    ),
    [BLOCKS.OL_LIST]: (_, children) => (
      <Col role="list" className="gap-1.5 pl-5">
        {children}
      </Col>
    ),
    [BLOCKS.LIST_ITEM]: (_, children) => <Col role="listitem">{children}</Col>,
    [BLOCKS.QUOTE]: (_, children) => (
      <Row className="gap-2 border-l-[3px] border-od-border-primary pl-3">
        <Col className="gap-2">{children}</Col>
      </Row>
    ),
    [INLINES.HYPERLINK]: (node, children) => (
      <Link
        href={node.data.uri}
        analyticsName={`cosmos-landing-page-v2-narrative-lockups-${analyticsId ?? 'link'}`}
      >
        {children}
      </Link>
    ),
  },
});

const NarrativeLockup = ({
  lockup,
  analyticsId,
  showTopline,
}: {
  lockup: ILpElementNarrativeLockup;
  analyticsId?: string;
  showTopline: boolean;
}) => {
  const { eyebrow, title, body } = lockup.fields;

  if (!eyebrow && !title && (!body || isEmptyRichText(body))) {
    return null;
  }

  return (
    <Col
      className={cn(
        'gap-5 lg:flex-row lg:gap-12',
        showTopline && 'border-t border-od-border-primary pt-7',
      )}
    >
      <Col className="gap-1.5 lg:shrink-0 lg:basis-80">
        {eyebrow && (
          <Text as="p" className="text-xs font-medium text-od-content-tertiary uppercase">
            {eyebrow}
          </Text>
        )}
        {title && (
          <Text as="h2" className="text-header-sm text-od-content-primary">
            {title}
          </Text>
        )}
      </Col>
      {body && !isEmptyRichText(body) && (
        <Col className="max-w-220 flex-1 gap-4">
          {documentToReactComponents(body, richTextOptions(analyticsId))}
        </Col>
      )}
    </Col>
  );
};

export const RenderNarrativeLockups = (entry: ILpComponentNarrativeLockups) => {
  const { analyticsId, eyebrow, title, intro, items } = entry.fields;
  const showTopline = entry.fields.showTopline ?? true;

  if (!items?.length) {
    return null;
  }

  return (
    <Container noSectionSpacing className="py-10">
      <Col className="gap-18">
        {(eyebrow || title || (intro && !isEmptyRichText(intro))) && (
          <Col className="max-w-50 gap-2">
            {eyebrow && (
              <Text as="p" className="text-xs font-medium text-od-content-tertiary uppercase">
                {eyebrow}
              </Text>
            )}
            {title && (
              <Text
                as="h2"
                className="text-header-md text-od-content-primary max-md:text-header-sm"
              >
                {title}
              </Text>
            )}
            {intro && !isEmptyRichText(intro) && (
              <Col className="gap-2">
                {documentToReactComponents(intro, richTextOptions(analyticsId))}
              </Col>
            )}
          </Col>
        )}
        <Col className="gap-18">
          {items.map((lockup, index) => (
            <NarrativeLockup
              key={lockup.sys?.id ?? index}
              lockup={lockup}
              analyticsId={analyticsId}
              showTopline={showTopline}
            />
          ))}
        </Col>
      </Col>
    </Container>
  );
};

const NarrativeLockups: EntryComponent<ILpComponentNarrativeLockups> = {
  render: RenderNarrativeLockups,
};

export default NarrativeLockups;
